import { HardHat, Plus, Trash2 } from "lucide-react";
import { Button, IconButton, Pill, TH, TD, TD_NUM } from "../../../../components/ui";
import { fmtCurrency, fmtPct } from "../../../../utils/format";
import SectionShell, { SubSection } from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellPct, CellText } from "../cells";

export default function LaborBuildupSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "laborBuildup", onSaved, "Labor build-up");
  const lb = draft.value;
  if (!lb) return null;

  const base = Number(lb.baseWage) || 0;
  const burdenPct = lb.items.reduce((s, i) => s + (Number(i.pct) || 0), 0);
  const burdened = base * (1 + burdenPct);
  const configured = config.globalAssumptions?.laborRatePainting || 0;
  const diff = burdened - configured;
  const matches = Math.abs(diff) < 0.05;

  const addItem = () =>
    draft.update((d) => {
      d.items.push({ key: `item-${Date.now().toString(36)}`, label: "New burden item", pct: 0 });
    });

  return (
    <SectionShell
      title="Labor Build-up"
      subtitle="Base wage plus payroll burden → burdened hourly rate."
      icon={HardHat}
      draft={draft}
      readOnly={readOnly}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <SubSection title="Burden items" hint="Each item is a percentage of the base wage.">
          <div className="overflow-x-auto rounded-[4px] border border-border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>Item</th>
                  <th className={`${TH} w-32 text-right`}>Rate</th>
                  <th className={`${TH} w-28 text-right`}>$/hour</th>
                  {!readOnly && <th className={`${TH} w-12`} aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={`${TD} font-semibold`}>Base wage</td>
                  <td className={`${TD} py-1`}>
                    <CellNumber
                      value={lb.baseWage}
                      onChange={(v) => draft.update((d) => { d.baseWage = v; })}
                      disabled={readOnly}
                      step="0.01"
                      min={0}
                      suffix="$"
                      ariaLabel="Base wage"
                    />
                  </td>
                  <td className={TD_NUM}>{fmtCurrency(base)}</td>
                  {!readOnly && <td className={TD} />}
                </tr>
                {lb.items.map((item, i) => (
                  <tr key={item.key}>
                    <td className={`${TD} py-1`}>
                      <CellText
                        value={item.label}
                        onChange={(v) => draft.update((d) => { d.items[i].label = v; })}
                        disabled={readOnly}
                        ariaLabel="Burden item name"
                      />
                    </td>
                    <td className={`${TD} py-1`}>
                      <CellPct
                        value={item.pct}
                        onChange={(v) => draft.update((d) => { d.items[i].pct = v; })}
                        disabled={readOnly}
                        min={0}
                        ariaLabel={`${item.label} rate`}
                      />
                    </td>
                    <td className={TD_NUM}>{fmtCurrency(base * (Number(item.pct) || 0))}</td>
                    {!readOnly && (
                      <td className={`${TD} py-1`}>
                        <IconButton
                          icon={Trash2}
                          size="xs"
                          variant="ghost"
                          label={`Remove ${item.label}`}
                          onClick={() => draft.update((d) => { d.items.splice(i, 1); })}
                        />
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="bg-surface_alt">
                  <td className={`${TD} font-semibold`}>Total burden</td>
                  <td className={`${TD_NUM} font-semibold`}>{fmtPct(burdenPct)}</td>
                  <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(base * burdenPct)}</td>
                  {!readOnly && <td className={TD} />}
                </tr>
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div>
              <Button size="xs" icon={Plus} onClick={addItem}>
                Add burden item
              </Button>
            </div>
          )}
        </SubSection>

        <div className="flex flex-col gap-3 self-start rounded-[4px] border border-border bg-surface_alt p-4" aria-live="polite">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text_muted">Burdened rate</span>
          <span className="text-[28px] font-bold leading-none tabular-nums text-text_primary">
            {fmtCurrency(burdened)}
            <span className="ml-1 text-[13px] font-medium text-text_muted">/ hr</span>
          </span>
          <span className="text-[12px] text-text_secondary">
            {fmtCurrency(base)} × (1 + {fmtPct(burdenPct)})
          </span>
          <div className="border-t border-border pt-3 text-[12px] text-text_secondary">
            <div className="flex items-center justify-between">
              <span>Painting rate in Global Assumptions</span>
              <span className="font-semibold tabular-nums text-text_primary">{fmtCurrency(configured)}</span>
            </div>
            <div className="mt-2">
              {matches ? (
                <Pill tone="success" dot>
                  In sync
                </Pill>
              ) : (
                <Pill tone="warning" dot>
                  {diff > 0 ? "+" : "−"}
                  {fmtCurrency(Math.abs(diff))} vs. configured rate
                </Pill>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
